
import { useState, useMemo } from "react";
import { usePagination } from "@/hooks/usePagination";
import { EpisodeCategory } from "../episodes/types";

interface UseCategoriesManagerProps {
  categories: EpisodeCategory[];
  searchTerm: string;
  filters: {
    status: string;
  };
  saveCategory: (categoryData: any, editingCategoryId?: string) => Promise<void>;
}

export const useCategoriesManager = ({ 
  categories, 
  searchTerm, 
  filters, 
  saveCategory 
}: UseCategoriesManagerProps) => {
  const [editingCategory, setEditingCategory] = useState<EpisodeCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Filter and search categories
  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          category.name.toLowerCase().includes(searchLower) ||
          category.description?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status && category.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [categories, searchTerm, filters]);

  // Pagination
  const pagination = usePagination({
    items: filteredCategories,
    itemsPerPage: 10,
  });

  const handleSave = async (categoryData: any) => {
    await saveCategory(categoryData, editingCategory?.id);
    setEditingCategory(null);
    setIsCreating(false);
  };

  const startCreating = () => setIsCreating(true);
  const startEditing = (category: EpisodeCategory) => setEditingCategory(category);
  const cancelEditing = () => {
    setEditingCategory(null);
    setIsCreating(false);
  };

  return {
    editingCategory,
    isCreating,
    filteredCategories,
    pagination,
    handleSave,
    startCreating,
    startEditing,
    cancelEditing,
  };
};
